import {readWireMinimumPrice, type WireMinimumPrice} from "@/features/pricing";
import type {CoverImagesItemResponse} from "../../../products/types";
import type {SubscriptionTierEntity} from "../tiers/types";
import type {GetSubscriptionApiResponse, SubscriptionSubscriberEntity} from "./types";

function mapSubscriber(raw: unknown): SubscriptionSubscriberEntity | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const id = Number(o.id ?? o.subscriberId ?? 0);
  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }
  const user =
    o.user && typeof o.user === "object" ? (o.user as Record<string, unknown>) : null;
  const displayFromUser = user
    ? String(user.displayName ?? user.name ?? user.fullName ?? "")
    : "";
  return {
    id,
    userId:
      typeof o.userId === "number"
        ? o.userId
        : typeof o.user_id === "number"
        ? o.user_id
        : typeof user?.id === "number"
        ? user.id
        : undefined,
    email:
      typeof o.email === "string"
        ? o.email
        : typeof user?.email === "string"
        ? user.email
        : undefined,
    displayName:
      (typeof o.displayName === "string" && o.displayName) ||
      (typeof o.name === "string" && o.name) ||
      displayFromUser ||
      undefined,
    status: o.status as string | number | undefined,
    subscribedAt:
      typeof o.subscribedAt === "string"
        ? o.subscribedAt
        : typeof o.subscribed_at === "string"
        ? o.subscribed_at
        : typeof o.createdAt === "string"
        ? o.createdAt
        : undefined,
  };
}

function parseSubscribers(raw: unknown): SubscriptionSubscriberEntity[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(mapSubscriber)
    .filter((s): s is SubscriptionSubscriberEntity => s !== null);
}

function mapTier(
  raw: unknown,
  subscriptionId: number,
  fallbackIndex: number
): SubscriptionTierEntity {
  if (!raw || typeof raw !== "object") {
    return {
      id: fallbackIndex,
      subscriptionId,
      name: "",
      billingInterval: 3,
      billingDuration: 1,
      billingCycles: null,
      defaultPrice: 0,
      defaultCurrency: "USD",
      currencies: [{callPriceCurrency: "USD", callPrice: 0}],
      selectedCurrencies: ["USD"],
      features: [],
      sortOrder: fallbackIndex,
      isPayWhatYouWant: false,
      createdAt: "",
      updatedAt: "",
    };
  }
  const o = raw as Record<string, unknown>;
  const featuresRaw = o.features;
  const features = Array.isArray(featuresRaw)
    ? featuresRaw.map((f) => (typeof f === "string" ? f : String(f)))
    : [];
  const cyclesRaw = o.billingCycles ?? o.billing_cycles;
  const billingCycles =
    cyclesRaw === null || cyclesRaw === undefined ? null : Number(cyclesRaw);
  const defaultCurrency = String(o.defaultCurrency ?? o.default_currency ?? "USD");
  const defaultPrice = Number(o.defaultPrice ?? o.default_price ?? 0);
  const isPayWhatYouWant = o.isPayWhatYouWant === true;
  // Written as `minCallPrice`, read back as `minAmount`: see `readWireMinimumPrice`.
  const defaultMinPrice = readWireMinimumPrice(o.defaultMinPrice as WireMinimumPrice);
  const rawCurrencies = Array.isArray(o.currencies) ? o.currencies : [];
  const currencies =
    rawCurrencies.length > 0
      ? (() => {
          const defaultUpper = defaultCurrency.toUpperCase();
          const mapped = rawCurrencies.map((currency) => {
            const c =
              currency && typeof currency === "object"
                ? (currency as Record<string, unknown>)
                : {};
            const minCallPrice = readWireMinimumPrice(
              (c.minAmount ?? c.minCallPrice) as WireMinimumPrice
            );
            return {
              callPriceCurrency: String(
                c.callPriceCurrency ??
                  c.call_price_currency ??
                  c.currency ??
                  defaultCurrency
              ),
              callPrice: Number(c.callPrice ?? c.call_price ?? c.price ?? c.amount ?? 0),
              ...(minCallPrice === undefined ? {} : {minCallPrice}),
              isAutoConverted: c.isAutoConverted === true || c.is_auto_converted === true,
              is_auto_converted:
                c.is_auto_converted === true || c.isAutoConverted === true,
            };
          });
          mapped.sort((a, b) => {
            const aDef = a.callPriceCurrency.toUpperCase() === defaultUpper;
            const bDef = b.callPriceCurrency.toUpperCase() === defaultUpper;
            if (aDef && !bDef) {
              return -1;
            }
            if (!aDef && bDef) {
              return 1;
            }
            return 0;
          });
          return mapped;
        })()
      : [
          {
            callPriceCurrency: defaultCurrency,
            callPrice: defaultPrice,
            ...(defaultMinPrice === undefined ? {} : {minCallPrice: defaultMinPrice}),
          },
        ];
  const selectedCurrencies = Array.from(
    new Set(currencies.map((currency) => currency.callPriceCurrency).filter(Boolean))
  );
  const redirectType =
    o.redirectType === "telegram" || o.redirectType === "url" || o.redirectType === "none"
      ? o.redirectType
      : undefined;
  const telegramGroupChatIdRaw = o.telegramGroupChatId ?? o.telegram_group_chat_id;
  const telegramGroupChatId =
    telegramGroupChatIdRaw === null
      ? null
      : typeof telegramGroupChatIdRaw === "string"
      ? telegramGroupChatIdRaw
      : undefined;
  const urlRedirectRaw = Object.prototype.hasOwnProperty.call(o, "urlRedirect")
    ? o.urlRedirect
    : Object.prototype.hasOwnProperty.call(o, "url_redirect")
    ? o.url_redirect
    : Object.prototype.hasOwnProperty.call(o, "redirectUrl")
    ? o.redirectUrl
    : o.redirect_url;
  const urlRedirect =
    urlRedirectRaw === null
      ? null
      : typeof urlRedirectRaw === "string"
      ? urlRedirectRaw
      : undefined;

  return {
    id: Number(o.id ?? fallbackIndex),
    subscriptionId: Number(o.subscriptionId ?? o.subscription_id ?? subscriptionId),
    name: String(o.name ?? ""),
    billingInterval: Number(o.billingInterval ?? o.billing_interval ?? 3),
    billingDuration: Number(o.billingDuration ?? o.billing_duration ?? 1),
    billingCycles: Number.isFinite(billingCycles) ? billingCycles : null,
    defaultPrice,
    defaultCurrency,
    currencies,
    selectedCurrencies,
    features,
    sortOrder: Number(o.sortOrder ?? o.sort_order ?? fallbackIndex),
    isPayWhatYouWant,
    ...(defaultMinPrice === undefined ? {} : {defaultMinPrice}),
    redirectType,
    telegramGroupChatId,
    urlRedirect,
    createdAt: String(o.createdAt ?? o.created_at ?? ""),
    updatedAt: String(o.updatedAt ?? o.updated_at ?? ""),
    deletedAt:
      o.deletedAt === null || o.deleted_at === null
        ? null
        : typeof o.deletedAt === "string"
        ? o.deletedAt
        : typeof o.deleted_at === "string"
        ? o.deleted_at
        : undefined,
  };
}

export function parseTiers(
  raw: unknown,
  subscriptionId: number
): SubscriptionTierEntity[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((item, index) => mapTier(item, subscriptionId, index));
}

function mapCover(raw: unknown, index: number): CoverImagesItemResponse {
  if (!raw || typeof raw !== "object") {
    return {
      id: String(index),
      media_url: "",
      offering_id: 0,
      createdAt: "",
      updatedAt: "",
      media_type: "image",
      offering_type: "product",
      user_id: 0,
    };
  }
  const o = raw as Record<string, unknown>;
  return {
    id: String(o.id ?? o.mediaId ?? index),
    media_url: String(o.media_url ?? o.mediaUrl ?? o.url ?? ""),
    offering_id: Number(o.offering_id ?? o.offeringId ?? 0),
    createdAt: String(o.createdAt ?? ""),
    updatedAt: String(o.updatedAt ?? ""),
    media_type: "image",
    offering_type: "product",
    user_id: Number(o.user_id ?? o.userId ?? 0),
  };
}

/**
 * API returns `GET subscriptions/:id` payload on `data` (flat object with slug, cover_images, …).
 * Older clients expected `{ productSubscription }` — normalize both shapes.
 */
export function normalizeGetSubscriptionResponse(
  inner: unknown
): GetSubscriptionApiResponse {
  if (!inner || typeof inner !== "object") {
    return {
      productSubscription: {
        id: 0,
        name: "",
        slug: "",
        description: "",
        cover_images: [],
        createdAt: "",
        updatedAt: "",
        tiers: [],
        subscribers: [],
      },
    };
  }
  const o = inner as Record<string, unknown>;
  if (o.productSubscription && typeof o.productSubscription === "object") {
    return normalizeGetSubscriptionResponse(o.productSubscription);
  }
  const name = String(o.name ?? "");
  const slug =
    typeof o.slug === "string" && o.slug
      ? o.slug
      : name.toLowerCase().trim().replace(/\s+/g, "-") || "";

  const id = Number(o.id ?? 0);

  const rawImages = o.cover_images ?? o.coverImages;
  let cover_images = Array.isArray(rawImages)
    ? rawImages.map((img, i) => mapCover(img, i))
    : [];
  if (
    cover_images.length === 0 &&
    typeof o.coverImage === "string" &&
    o.coverImage.trim() !== ""
  ) {
    cover_images = [
      mapCover({id: "coverImage", media_url: o.coverImage, offering_id: id}, 0),
    ];
  }

  let redirectUrl: string | null | undefined;
  if (o.redirectUrl === null) {
    redirectUrl = null;
  } else if (typeof o.redirectUrl === "string") {
    redirectUrl = o.redirectUrl;
  }

  const rawBooking = o.bookingQuestions;
  const bookingQuestions: string[] = Array.isArray(rawBooking)
    ? rawBooking
        .map((item) => {
          if (typeof item === "string") {
            return item;
          }
          if (item && typeof item === "object" && "question" in item) {
            return String((item as {question: unknown}).question);
          }
          return "";
        })
        .filter(Boolean)
    : [];

  return {
    productSubscription: {
      id,
      name,
      slug,
      description: String(o.description ?? ""),
      cover_images,
      createdAt: String(o.createdAt ?? ""),
      updatedAt: String(o.updatedAt ?? ""),
      tiny_url: typeof o.tiny_url === "string" ? o.tiny_url : undefined,
      redirectUrl,
      hideFromProfile:
        typeof o.hideFromProfile === "boolean" ? o.hideFromProfile : undefined,
      requestPhoneNumber:
        typeof o.requestPhoneNumber === "boolean" ? o.requestPhoneNumber : undefined,
      bookingQuestions,
      enableCurrencyConversion:
        typeof o.enableCurrencyConversion === "boolean"
          ? o.enableCurrencyConversion
          : undefined,
      selectedCurrencies: (() => {
        const raw = o.selectedCurrencies ?? o.selected_currencies;
        if (!Array.isArray(raw)) {
          return undefined;
        }
        const codes = raw
          .map((item) =>
            typeof item === "string" ? item.toUpperCase().trim() : String(item)
          )
          .filter(Boolean);
        return codes.length > 0 ? codes : undefined;
      })(),
      defaultCurrency:
        typeof o.defaultCurrency === "string" ? o.defaultCurrency : undefined,
      totalRevenue: typeof o.totalRevenue === "string" ? o.totalRevenue : undefined,
      activeSubscribers:
        typeof o.activeSubscribers === "number" ? o.activeSubscribers : undefined,
      tiers: parseTiers(o.tiers, id),
      subscribers: parseSubscribers(o.subscribers ?? o.subscriptionSubscribers),
    },
  };
}
